import { BuildClass, MaybeT, multiMap, MultiMapError, t, TW_OVERFLOW_ELLIPSIS, } from '../../../universal';
import { _, React } from '../../lib';
import { Alerts } from '../component-main';
import { uploadFileIris } from '../component-upload';
import { LoadingSpinnerLarge } from './loading';
import { CJSX, MatchRender, useRSInstance, useStateSync } from './meta-types';
import { stubInput } from './stubs';
export const FileUploadItemT = t.Object({
    Filename: t.String(),
    Checksum: MaybeT(t.String()),
    FileGuid: t.Optional(MaybeT(t.String())),
    Data: MaybeT(t.String()),
});
export var FileType;
(function (FileType) {
    FileType[FileType["Doc"] = 0] = "Doc";
    FileType[FileType["PDF"] = 1] = "PDF";
    FileType[FileType["Spreadsheet"] = 2] = "Spreadsheet";
    FileType[FileType["Presentation"] = 3] = "Presentation";
    FileType[FileType["CSV"] = 4] = "CSV";
    FileType[FileType["TSV"] = 5] = "TSV";
    FileType[FileType["XML"] = 6] = "XML";
    FileType[FileType["AnyAudio"] = 7] = "AnyAudio";
    FileType[FileType["AnyVideo"] = 8] = "AnyVideo";
    FileType[FileType["AnyImage"] = 9] = "AnyImage";
    FileType[FileType["JSON"] = 10] = "JSON";
    FileType[FileType["ZIP"] = 11] = "ZIP";
})(FileType || (FileType = {}));
const getFileTypeValues = (fileKind) => {
    switch (fileKind) {
        case FileType.Doc:
            return [
                '.docx',
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            ];
        case FileType.PDF:
            return ['.pdf'];
        case FileType.CSV:
            return ['.csv'];
        case FileType.TSV:
            return ['.tsv'];
        case FileType.JSON:
            return ['.json', 'application/json'];
        case FileType.Spreadsheet:
            return [
                '.xlsx',
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            ];
        case FileType.Presentation:
            return [
                '.pptx',
                'application/vnd.ms-powerpoint',
                'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
                'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            ];
        case FileType.XML:
            return ['.xml', 'application/xml', 'text/xml'];
        case FileType.AnyAudio:
            return ['audio/*'];
        case FileType.AnyVideo:
            return ['video/*'];
        case FileType.AnyImage:
            return ['image/*'];
        case FileType.ZIP:
            return ['.zip', 'application/zip'];
    }
};
const displayFileTypesList = (fileTypes) => _(fileTypes ?? [])
    .map(kind => getFileTypeValues(kind))
    .flatten()
    .compact()
    .join(',') || undefined;
const getDefaultState = (p) => ({
    files: p.value ?? [],
    isUploadingProgress: null,
    isDraggingOver: false,
    isFocused: false,
});
export const FileUpload = (props) => {
    const rs = useRSInstance({
        props,
        defaultState: getDefaultState,
        refs: {
            inputRef: React.useRef(stubInput),
        },
        actionToDelta: null,
    });
    const getFiles = (files) => files.filter(x => {
        const storageMode = props.storageMode ?? 'v2';
        if (storageMode === 'v3') {
            return x.FileGuid != null;
        }
        else {
            if (x.Checksum == null) {
                return true;
            }
            return !props.ignoreChecksums?.includes(x.Checksum);
        }
    });
    useStateSync({
        propVal: props.value,
        stateVal: rs.state.files,
        setState: files => {
            rs.updateState({ files });
        },
        setProp: files => {
            props.onUpdate(files);
        },
    });
    const uploadFilesWrapped = (fileList) => {
        if (props.onUpload) {
            rs.updateState({ isUploadingProgress: 0 });
            props
                .onUpload(fileList)
                .then(uploadedFiles => {
                rs.updateState({
                    files: getFiles(uploadedFiles),
                    isUploadingProgress: null,
                });
                rs.props.onUpdate(getFiles(uploadedFiles));
            })
                .catch(error => {
                rs.updateState({ isUploadingProgress: null });
                console.error('Upload failed:', error);
            });
            return;
        }
        uploadFiles({
            fileList: fileList,
            ignoreChecksums: props.ignoreChecksums,
            storageMode: props.storageMode,
            onStart: () => {
                rs.updateState({ isUploadingProgress: 0 });
            },
            onUpdateProgress: perc => {
                rs.updateState({ isUploadingProgress: perc });
            },
            onComplete: uploadedFiles => {
                rs.updateState({
                    files: getFiles(uploadedFiles),
                    isUploadingProgress: null,
                });
                rs.props.onUpdate(getFiles(uploadedFiles));
            },
        }).catch(error => {
            rs.updateState({ isUploadingProgress: null });
            console.error('Upload failed:', error);
        });
    };
    return (React.createElement("div", { className: BuildClass({
            [BuildClass(rs.props.className)]: true,
            'relative h-full font-light text-[#888]': true,
            'border-2 border-dashed rounded-md': true,
            'cursor-pointer': true,
            'overflow-y-auto': true,
            'bg-[#ffe] hover:bg-[#ffb]': !rs.state.isDraggingOver && !rs.state.isFocused,
            'bg-[#ffb]': rs.state.isFocused && !rs.state.isDraggingOver,
            'bg-[#ff9]': rs.state.isDraggingOver,
            'border-[#ccc] hover:border-[#aaa]': !rs.state.isDraggingOver && !rs.state.isFocused,
            'border-[#00f]': rs.state.isFocused && !rs.state.isDraggingOver,
            'border-[#000]': rs.state.isDraggingOver,
        }), style: { height: rs.props.height }, onDragEnter: ev => {
            ev.preventDefault();
            rs.updateState({ isDraggingOver: true });
        }, onDragOver: ev => {
            ev.preventDefault();
            rs.updateState({ isDraggingOver: true });
        }, onDragLeave: ev => {
            ev.preventDefault();
            rs.updateState({ isDraggingOver: false });
        }, onDrop: ev => {
            ev.preventDefault();
            rs.updateState({ isDraggingOver: false });
            uploadFilesWrapped(ev.dataTransfer.files);
        } },
        React.createElement("input", { className: "pointer-events-none w-0 h-0 overflow-hidden", type: "file", ref: rs.refs.inputRef, multiple: rs.props.multiple, onFocus: () => {
                rs.updateState({ isFocused: true });
            }, onBlur: () => {
                rs.updateState({ isFocused: false });
            }, onChange: async (ev) => {
                const cnt = ev.target.files?.length ?? 0;
                if (cnt === 0) {
                    console.info('No files selected when changing input');
                    return;
                }
                uploadFilesWrapped(ev.target.files);
                window.requestAnimationFrame(() => {
                    ev.target.value = '';
                });
            }, accept: displayFileTypesList(rs.props.accept) }),
        React.createElement("span", { className: "absolute inline-block top-1/2 w-full translate-y-[-50%] text-center" },
            React.createElement(MatchRender, { check: [
                    {
                        when: rs.state.isUploadingProgress !== null,
                        then: React.createElement(React.Fragment, null,
                            "Uploading ",
                            rs.state.isUploadingProgress,
                            "%"),
                    },
                    {
                        when: rs.state.isDraggingOver,
                        then: React.createElement(React.Fragment, null, "Drop to upload"),
                    },
                    {
                        when: rs.state.files.length > 0,
                        then: (React.createElement(React.Fragment, null, rs.state.files.map(file => {
                            const storageMode = rs.props.storageMode ?? 'v2';
                            const key = storageMode === 'v3'
                                ? (file.FileGuid ??
                                    `unknown-${Date.now()}`)
                                : (file.Checksum ??
                                    `unknown-${Date.now()}`);
                            return (React.createElement(AttachedFileLine, { key: key, file: file, storageMode: storageMode, onRemove: () => {
                                    const identifier = storageMode === 'v3'
                                        ? file.FileGuid
                                        : file.Checksum;
                                    if (!identifier) {
                                        Alerts.Alert({
                                            msg: 'Cannot remove file: missing identifier',
                                        });
                                        return;
                                    }
                                    const files = rs.state.files.filter(f => {
                                        if (storageMode === 'v3') {
                                            return (f.FileGuid !==
                                                identifier);
                                        }
                                        else {
                                            return (f.Checksum !==
                                                identifier);
                                        }
                                    });
                                    rs.updateState({
                                        files: getFiles(files),
                                    });
                                    rs.props.onUpdate(getFiles(files));
                                } }));
                        }))),
                    },
                    {
                        when: true,
                        then: (React.createElement(React.Fragment, null, rs.props.placeholder ?? 'Click or drag files here')),
                    },
                ] }))));
};
const AttachedFileLine = (props) => {
    const [isDownloadHovered, setIsDownloadHovered] = React.useState(false);
    const [isDeleteHovered, setIsDeleteHovered] = React.useState(false);
    const handleDownload = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const storageMode = props.storageMode ?? 'v2';
        const identifier = storageMode === 'v3' ? props.file.FileGuid : props.file.Checksum;
        if (!identifier) {
            Alerts.Alert({ msg: 'Cannot download file: missing identifier' });
            return;
        }
        const endpoint = storageMode === 'v3' ? 'get-v3' : 'get';
        fetch(`https://storage.trionline.com.au/${endpoint}/${identifier}/`)
            .then(async (x) => x.blob())
            .then(file => {
            const url = window.URL.createObjectURL(file);
            const a = document.createElement('a');
            a.href = url;
            a.download = props.file.Filename || 'download';
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            }, 0);
        })
            .catch(err => {
            Alerts.Alert({ msg: `Failed to download file: ${String(err)}` });
        });
    };
    return (React.createElement("div", { className: BuildClass({
            'flex items-center m-1 rounded-xs': true,
            'bg-red-200': isDeleteHovered,
            'bg-blue-200': isDownloadHovered,
            'hover:bg-[#9df]': !isDeleteHovered && !isDownloadHovered,
        }) },
        React.createElement("span", { className: BuildClass({
                [TW_OVERFLOW_ELLIPSIS]: true,
                'flex-grow align-baseline': true,
                'leading-6 font-normal': true,
                'text-neutral-800': true,
            }) }, props.file.Filename),
        React.createElement("span", { className: BuildClass({
                'shrink-0 align-baseline': true,
                'w-6 h-6 p-1 ml-1': true,
                'border border-transparent rounded-xs': true,
                'cursor-pointer hover:border-[#00F] hover:bg-blue-300': true,
            }), onMouseEnter: () => {
                setIsDownloadHovered(true);
            }, onMouseLeave: () => {
                setIsDownloadHovered(false);
            }, onClick: handleDownload },
            React.createElement("img", { className: "w-4 h-4", src: "/static/img/i8/color-copy.svg", alt: "Download attachment" })),
        React.createElement("span", { className: BuildClass({
                'shrink-0 align-baseline': true,
                'w-6 h-6 p-1 ml-1': true,
                'border border-transparent rounded-xs': true,
                'cursor-pointer hover:border-[#f00] hover:bg-red-300': true,
            }), onMouseEnter: () => {
                setIsDeleteHovered(true);
            }, onMouseLeave: () => {
                setIsDeleteHovered(false);
            }, onClick: e => {
                e.preventDefault();
                e.stopPropagation();
                props.onRemove();
            } },
            React.createElement("img", { className: "w-4 h-4", src: "/static/img/i8/w11-minus.svg", alt: "Remove attachment" }))));
};
const uploadFiles = async (input) => {
    input.onStart();
    const files = Array.from(input.fileList ?? []);
    const progress = _.times(files.length, () => 0);
    const filesWithIndex = files.map((file, index) => ({ file, index }));
    const storageMode = input.storageMode ?? 'v2';
    const uploadedFiles = await multiMap(filesWithIndex, storageMode === 'v3' ? 1 : 8, async ({ file, index }) => new Promise(async (resolve, reject) => {
        if (storageMode === 'v3') {
            const uploader = uploadFileIris({
                fileObj: file,
                storageMode: 'v3',
                onStart: () => {
                    progress[index] = 0;
                    const perc = Math.floor(_.sum(progress) / files.length);
                    input.onUpdateProgress(perc);
                },
                onError: err => {
                    Alerts.Alert({
                        msg: `Failed uploading ${file.name}: ${String(err)}`,
                    });
                    reject(err);
                },
                onComplete: async (e) => {
                    progress[index] = 100;
                    const perc = Math.floor(_.sum(progress) / files.length);
                    input.onUpdateProgress(perc);
                    if (!e.guid) {
                        const msg = 'Upload returned a v2 checksum instead of a v3 GUID. The active IRIS upload server needs the v3 storage-mode change.';
                        Alerts.Alert({ msg });
                        reject(new Error(msg));
                        return;
                    }
                    resolve({
                        Filename: e.file.name,
                        Checksum: null,
                        FileGuid: e.guid,
                        Data: null,
                        MimeType: e.file.type,
                    });
                },
            });
            uploader();
        }
        else {
            const uploader = uploadFileIris({
                fileObj: file,
                ignoreChecksums: input.ignoreChecksums ?? [],
                onProgress: e => {
                    progress[index] = e.percentage;
                    const perc = Math.floor(_.sum(progress) / files.length);
                    input.onUpdateProgress(perc);
                },
                onError: err => {
                    Alerts.Alert({
                        msg: `Failed uploading ${file.name}: ${String(err)}`,
                    });
                    reject(err);
                },
                onComplete: async (e) => {
                    resolve({
                        Filename: e.file.name,
                        Checksum: e.checksum,
                        Data: Buffer.from(await e.file.arrayBuffer()).toString('base64'),
                        MimeType: e.file.type,
                    });
                },
            });
            uploader();
        }
    }));
    if (uploadedFiles.failed > 0) {
        const error = new Error(`Failed to upload ${uploadedFiles.failed} of ${files.length} files`);
        Alerts.Alert({ msg: error.message });
        throw error;
    }
    const uploaded = _(uploadedFiles.output)
        .compact()
        .filter(x => {
        const storageMode = input.storageMode ?? 'v2';
        return storageMode === 'v3' ? Boolean(x.FileGuid) : Boolean(x.Checksum);
    })
        .value();
    input.onComplete(uploaded);
    return uploaded;
};
const fileToDataOnlyUploadItem = async (file) => ({
    Filename: file.name,
    Checksum: null,
    Data: Buffer.from(await file.arrayBuffer()).toString('base64'),
    MimeType: file.type,
});
const readFileListAsDataOnlyUploadItems = async (fileList) => {
    const files = Array.from(fileList ?? []);
    if (files.length === 0) {
        return [];
    }
    try {
        const loadedFiles = await multiMap(files, 8, async (file) => {
            try {
                return await fileToDataOnlyUploadItem(file);
            }
            catch (err) {
                const e = err instanceof Error ? err : new Error(String(err));
                Alerts.Alert({
                    msg: `Failed reading ${file.name}: ${e.message}`,
                });
                throw e;
            }
        });
        return _.compact(loadedFiles.output);
    }
    catch (err) {
        const failed = err instanceof MultiMapError ? err.items.failed : files.length;
        Alerts.Alert({ msg: `Failed to read ${failed} files` });
        return [];
    }
};
const getDefaultStateDropZone = () => ({
    isUploadingProgress: null,
    isDraggingOver: false,
    isFocused: false,
    isProcessing: false,
});
export const FileUploadDropZone = (props) => {
    const rs = useRSInstance({
        props,
        defaultState: getDefaultStateDropZone,
        refs: {
            inputRef: React.useRef(stubInput),
        },
        actionToDelta: null,
    });
    const uploadFilesWrapped = (fileList) => {
        if (props.skipStorageServer) {
            rs.updateState({ isUploadingProgress: null, isProcessing: true });
            readFileListAsDataOnlyUploadItems(fileList)
                .then(rs.props.onUpload)
                .catch(console.error)
                .finally(() => {
                rs.updateState({ isProcessing: false });
            });
            return;
        }
        uploadFiles({
            fileList: fileList,
            ignoreChecksums: props.ignoreChecksums,
            storageMode: props.storageMode ?? 'v2',
            onStart: () => {
                rs.updateState({ isUploadingProgress: 0 });
            },
            onUpdateProgress: perc => {
                rs.updateState({ isUploadingProgress: perc });
            },
            onComplete: uploadedFiles => {
                rs.updateState({
                    isUploadingProgress: null,
                    isProcessing: true,
                });
                rs.props
                    .onUpload(uploadedFiles)
                    .catch(console.error)
                    .finally(() => {
                    rs.updateState({ isProcessing: false });
                });
            },
        }).catch(error => {
            rs.updateState({
                isUploadingProgress: null,
                isProcessing: false,
            });
            console.error('Upload failed:', error);
        });
    };
    return (React.createElement("div", { className: BuildClass(['relative h-full', props.className]), onDragEnter: ev => {
            ev.preventDefault();
            if (!props.disabled) {
                rs.updateState({ isDraggingOver: true });
            }
        }, onDragOver: ev => {
            ev.preventDefault();
            if (!props.disabled) {
                rs.updateState({ isDraggingOver: true });
            }
        }, onDragLeave: ev => {
            ev.preventDefault();
            if (!props.disabled) {
                rs.updateState({ isDraggingOver: false });
            }
        }, onDrop: ev => {
            ev.preventDefault();
            if (!props.disabled) {
                rs.updateState({ isDraggingOver: false });
                uploadFilesWrapped(ev.dataTransfer.files);
            }
        } },
        React.createElement("div", { className: BuildClass({
                'absolute z-10 top-0 left-0 h-full w-full': true,
                'pointer-events-none opacity-0': !Boolean(rs.state.isDraggingOver ||
                    rs.state.isUploadingProgress !== null ||
                    rs.state.isProcessing),
            }) },
            React.createElement("input", { className: "pointer-events-none w-0 h-0 overflow-hidden", type: "file", tabIndex: -1, ref: rs.refs.inputRef, multiple: rs.props.multiple, onChange: async (ev) => {
                    if (!props.disabled) {
                        uploadFilesWrapped(ev.target.files);
                        ev.target.value = '';
                    }
                } }),
            React.createElement("div", { className: BuildClass({
                    'absolute z-10 top-0 left-0 m-[2px]': true,
                    'h-[calc(100%-4px)] w-[calc(100%-4px)]': true,
                    'text-center grid place-items-center': true,
                    'border-2 border-dashed rounded-md': true,
                    'border-[#000]': true,
                    'bg-[hsla(60,100%,80%,0.5)]': true,
                    'text-neutral-600 text-2xl': true,
                }) },
                React.createElement(CJSX, { cond: rs.state.isDraggingOver },
                    React.createElement(React.Fragment, null, props.draggingOverMessage ?? 'Drop to upload files')),
                React.createElement(CJSX, { cond: !rs.state.isDraggingOver },
                    React.createElement("div", { className: "text-center" },
                        React.createElement(LoadingSpinnerLarge, null),
                        React.createElement("div", { className: "text-neutral-600 text-2xl" },
                            React.createElement(CJSX, { cond: rs.state.isProcessing },
                                React.createElement(React.Fragment, null, "Processing...")),
                            React.createElement(CJSX, { cond: !rs.state.isProcessing },
                                React.createElement(React.Fragment, null,
                                    rs.state.isUploadingProgress,
                                    "%"))))))),
        props.children));
};
export const FileUploadTemp = (props) => (React.createElement(FileUpload, { ..._.omit(props, 'multiple', 'onUpload'), multiple: false, onUpload: async (fileList) => Promise.all(Array.from(fileList ?? []).map(fileToDataOnlyUploadItem)) }));
//# sourceMappingURL=file-upload.js.map